import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { MissionControlTab } from "./TenantListPanel";
import { TenantSavingsDisplay } from "./TenantSavingsDisplay";
import { TenantInventoryDisplay } from "./TenantInventoryDisplay";
import { TenantScheduleDisplay } from "./TenantScheduleDisplay";
import { useIsAdmin } from "hooks/settings";
import { TenantActionButtons } from "./TenantActionButtons";
import { useTenantSavings } from "./useTenantSavings";
import { useTenantInventory } from "./useTenantInventory";
import { useTenantSchedule } from "./useTenantSchedule";
import { useTenantProductMix } from "./useTenantProductMix";
import { TenantProductMixDisplay } from "./TenantProductMixDisplay";
import { TenantShredUsageDisplay } from "./TenantShredUsageDisplay";
import { MissionControlTenant } from "src/store/api/generatedApi";
import { useTenantShredUsage } from "./useTenantShredUsage";

export const TenantRow = ({
  tenant,
  activeTab,
  isSelected = false,
  onSelect,
  onHover,
  onClose,
}: {
  tenant: MissionControlTenant;
  activeTab: MissionControlTab;
  isSelected?: boolean;
  onSelect?: () => void;
  onHover?: (hovered: boolean) => void;
  onClose?: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();

  const isOpenEnabled = tenant.open_status === "enabled" || isAdmin;
  const showScrapEyesIcon = tenant.feature_flags.includes("scrap_eyes_icon");
  const hasHeatsDashboard = tenant.feature_flags.includes(
    "strategy_dashboard_heats"
  );
  const hasScrapReport = tenant.feature_flags.includes(
    "strategy_dashboard_inventory"
  );
  const {
    isLoading: savingsLoading,
    hasSavingsData,
    latestSavings,
    savingsDataPoints,
    formattedMonth,
  } = useTenantSavings(tenant.tenant_names);
  const {
    isLoading: inventoryLoading,
    hasInventoryData,
    latestInventory,
    inventoryDataPoints,
    formattedDate,
  } = useTenantInventory(tenant.tenant_names);
  const {
    isLoading: scheduleLoading,
    hasScheduleData,
    firstMonth,
    scheduleDataPoints,
    formattedMonth: scheduleFormattedMonth,
    mixGroupNames: scheduleMixGroupNames,
    mixGroupDataPoints: scheduleMixGroupDataPoints,
  } = useTenantSchedule(tenant.tenant_names);
  const {
    isLoading: productMixLoading,
    hasProductMixData,
    mixGroupNames,
    months: productMixMonths,
  } = useTenantProductMix(tenant.tenant_names);
  const {
    isLoading: shredUsageLoading,
    hasShredUsageData,
    latestShredUsage,
    shredUsageDataPoints,
    formattedMonth: shredUsageFormattedMonth,
  } = useTenantShredUsage(tenant.tenant_names);

  const loadingByTab: Record<MissionControlTab, boolean> = {
    savings: savingsLoading,
    inventory: inventoryLoading,
    schedule: scheduleLoading,
    steelMix: productMixLoading,
    shredUsage: shredUsageLoading,
  };
  const isActiveTabLoading = loadingByTab[activeTab];

  const showSavings =
    activeTab === "savings" && hasSavingsData && latestSavings;
  const showInventory =
    activeTab === "inventory" && hasInventoryData && latestInventory;
  const showSchedule =
    activeTab === "schedule" && hasScheduleData && firstMonth;
  const showProductMix = activeTab === "steelMix" && hasProductMixData;
  const showShredUsage =
    activeTab === "shredUsage" && hasShredUsageData && latestShredUsage;

  return (
    <Box
      onClick={onSelect}
      onMouseEnter={
        onHover
          ? () => {
              onHover(true);
            }
          : undefined
      }
      onMouseLeave={
        onHover
          ? () => {
              onHover(false);
            }
          : undefined
      }
      sx={{
        p: 1,
        cursor: onSelect ? "pointer" : "default",
        borderRadius: 2,
        transition: "all 0.15s ease",
        background: isSelected ? "rgba(0,0,0,0.04)" : "transparent",
        "&:hover": onSelect
          ? {
              background: "rgba(0,0,0,0.02)",
            }
          : undefined,
        borderBottom: onClose ? "none" : `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack gap={0.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ whiteSpace: "nowrap" }}
          >
            {tenant.long_name}
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5}>
            {onClose ? (
              <IconButton
                aria-label="Close"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {isActiveTabLoading ? (
            <Box sx={{ px: 1.5, py: 0.5 }}>
              <CircularProgress size={20} />
            </Box>
          ) : showSavings ? (
            <TenantSavingsDisplay
              specificSavings={latestSavings.specific_savings}
              formattedMonth={formattedMonth}
              dataPoints={savingsDataPoints}
            />
          ) : showInventory ? (
            <TenantInventoryDisplay
              totalInventoryMass={latestInventory.total_inventory_mass}
              formattedDate={formattedDate}
              dataPoints={inventoryDataPoints}
            />
          ) : showSchedule ? (
            <TenantScheduleDisplay
              scheduledTons={firstMonth.scheduled_tons}
              formattedMonth={scheduleFormattedMonth}
              dataPoints={scheduleDataPoints}
              mixGroupNames={scheduleMixGroupNames}
              mixGroupDataPoints={scheduleMixGroupDataPoints}
            />
          ) : showProductMix ? (
            <TenantProductMixDisplay
              mixGroupNames={mixGroupNames}
              months={productMixMonths}
            />
          ) : showShredUsage ? (
            <TenantShredUsageDisplay
              mixPercentage={latestShredUsage.mix_percentage}
              formattedMonth={shredUsageFormattedMonth}
              dataPoints={shredUsageDataPoints}
            />
          ) : activeTab === "inventory" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.5, py: 0.5 }}
            >
              {t("noInventoryData")}
            </Typography>
          ) : activeTab === "schedule" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.5, py: 0.5 }}
            >
              {t("noScheduleData")}
            </Typography>
          ) : activeTab === "steelMix" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.5, py: 0.5 }}
            >
              {t("noProductMixData")}
            </Typography>
          ) : activeTab === "shredUsage" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.5, py: 0.5 }}
            >
              {t("noShredUsageData")}
            </Typography>
          ) : null}
          <TenantActionButtons
            tenantName={tenant.tenant_names[0] ?? tenant.tenant_name}
            hasHeatsDashboard={hasHeatsDashboard}
            hasScrapReport={hasScrapReport}
            showScrapEyesIcon={showScrapEyesIcon}
            isOpenEnabled={isOpenEnabled}
            onClickCapture={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            sx={{ flexShrink: 0 }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
